import React, { Fragment, useState, useEffect, useRef } from "react";
import {
  FaPlus,
  FaFilter,
  FaMinus,
  FaChevronDown,
  FaTimes,
  FaEdit,
} from "react-icons/fa";
import {
  Accordion,
  AccordionItem,
  AccordionItemHeading,
  AccordionItemButton,
  AccordionItemPanel,
} from "react-accessible-accordion";
import CustomFooter from "../../components/customFooter";
import GetCarePlanById from "../../hooks/getCarePlanById";
import useCarePlanInitialScore from "../../hooks/getCarePlanInitialScore";
import GetFunctionalChecklist from "../../hooks/getFunctionalChecklist";
import {
  getScoreOptionPillProps,
  getScoreRgb,
  isValidScaleScore,
  scaleResolverFor,
} from "../../utils/scoringScale";
import moment from "moment";
import { formatInEt } from "../../utils/helper";
import Skeleton, { SkeletonTheme } from "react-loading-skeleton";
import "react-loading-skeleton/dist/skeleton.css";
import jsPDF from "jspdf";
import { applyPlugin } from "jspdf-autotable";
import { useReactToPrint } from "react-to-print";
import { toast } from "react-toastify";
import "react-toastify/ReactToastify.css";
import { Link } from "react-router-dom/cjs/react-router-dom.min";

// Apply the plugin to jsPDF
applyPlugin(jsPDF);

// Colored score pill — mirrors the design used on the review care plan page.
// The colour comes from the scale the row's heading is scored on, so an ICF or
// ten-point score is drawn in that scale's colours rather than the 1-5 ones.
const ScorePill = ({ value, scale }) => {
  const display = value == null || value === "" ? "N/A" : value;
  const pill = getScoreOptionPillProps(scale, Number(display));
  return (
    <div
      className={`rounded-pill d-flex align-items-center justify-content-center font-weight-bold mx-auto ${
        display === "N/A" ? "head_light" : pill.className
      }`}
      style={{
        width: "28px",
        height: "28px",
        fontSize: "12px",
        minWidth: "28px",
        ...(display === "N/A" ? {} : pill.style),
      }}
    >
      {display}
    </div>
  );
};

const getPdfScoreValue = (value, scoringScale) => {
  if (value === null || value === undefined || value === "" || value === "N/A") {
    return null;
  }
  const parsed = Math.floor(Number(value));
  return isValidScaleScore(scoringScale, parsed) ? parsed : null;
};

const drawPdfScoreCircle = (doc, cell, value, scale, scoringScale) => {
  const score = getPdfScoreValue(value, scoringScale);
  if (score === null) return;

  const [r, g, b] = getScoreRgb(scoringScale, score) || [175, 177, 184];
  const radius = 3.4 * scale;
  const centerX = cell.x + cell.width / 2;
  const centerY = cell.y + cell.height / 2;

  doc.setFillColor(r, g, b);
  doc.circle(centerX, centerY, radius, "F");
  doc.setTextColor(0, 0, 0);
  doc.setFont("Quicksand", "normal");
  doc.setFontSize(9 * scale);
  doc.text(String(score), centerX, centerY + 1.1 * scale, {
    align: "center",
  });
  doc.setFont("Quicksand", "normal");
};

const arrayBufferToBase64 = (buffer) => {
  const bytes = new Uint8Array(buffer);
  let binary = "";
  const chunkSize = 0x8000;

  for (let index = 0; index < bytes.length; index += chunkSize) {
    binary += String.fromCharCode(
      ...bytes.subarray(index, index + chunkSize),
    );
  }

  return btoa(binary);
};

let pdfAssetsPromise = null;

const loadPdfAssets = () => {
  if (!pdfAssetsPromise) {
    pdfAssetsPromise = Promise.all([
      fetch("/normal_logo.png").then((response) => {
        if (!response.ok) throw new Error("Unable to load the POMMe logo");
        return response.arrayBuffer();
      }),
      fetch("/fonts/Quicksand.ttf").then((response) => {
        if (!response.ok) throw new Error("Unable to load the Quicksand font");
        return response.arrayBuffer();
      }),
    ]).then(([logoBuffer, fontBuffer]) => ({
      logoDataUrl: `data:image/png;base64,${arrayBufferToBase64(logoBuffer)}`,
      quicksandFontBase64: arrayBufferToBase64(fontBuffer),
    }));
  }

  return pdfAssetsPromise;
};

const resetPdfPage = (doc) => {
  doc.setFillColor(255, 255, 255);
  doc.rect(
    0,
    0,
    doc.internal.pageSize.width,
    doc.internal.pageSize.height,
    "F",
  );
};

const addPdfHeader = (doc, logoDataUrl, patientData, carePlanData) => {
  const pageWidth = doc.internal.pageSize.getWidth();
  const scale = 0.75;
  const headerMarginX = 7;
  const patientRightX = pageWidth - headerMarginX;
  const carePlanType =
    carePlanData?.type === "INDIVIDUAL"
      ? "Individual Care Plan"
      : "Collaborative Care Plan";
  const creatorName = carePlanData?.author
    ? `${carePlanData.author.lastName}, ${carePlanData.author.firstName}`
    : "Unknown";
  const creatorPosition = carePlanData?.author?.position?.abbreviation || "N/A";

  resetPdfPage(doc);
  doc.setTextColor(0, 0, 0);
  doc.addImage(logoDataUrl, "PNG", headerMarginX, 7, 30, 13);

  doc.setFont("Quicksand", "normal");
  doc.setFontSize(16 * scale);
  doc.text(carePlanType, pageWidth / 2, 13, { align: "center" });

  const patientSummary = [
    patientData?.lastName && patientData?.firstName
      ? `${patientData.lastName}, ${patientData.firstName}`
      : patientData?.lastName || patientData?.firstName || "",
    patientData?.age == null ? "" : String(patientData.age),
    patientData?.gender || "",
  ]
    .filter(Boolean)
    .join(" ");

  doc.setFontSize(11 * scale);
  doc.text(patientSummary, patientRightX, 12, { align: "right" });
  doc.text(
    `DOB: ${
      patientData?.dob ? moment(patientData.dob).format("DD MMM YYYY") : ""
    }`,
    patientRightX,
    16,
    { align: "right" },
  );
  doc.text(`OHIP #: ${patientData?.ohip || ""}`, patientRightX, 20, {
    align: "right",
  });
  doc.setFontSize(11 * scale);
  doc.text(
    `Created by ${creatorName}: ${creatorPosition} ${
      carePlanData?.entryDate && moment(carePlanData.entryDate).isValid()
        ? moment(carePlanData.entryDate).format("DD MMM YYYY HH:mm")
        : ""
    }`,
    patientRightX,
    28,
    { align: "right" },
  );
};

const WeekConference = (props) => {
  // Interventions are expanded by default: opening a heading shows every
  // subheading's interventions. We track only the keys the user has explicitly
  // collapsed, so nothing is hidden unless they choose to hide it.
  const [collapsedIssues, setCollapsedIssues] = useState([]);
  const isIssueExpanded = (key) => !collapsedIssues.includes(key);
  const toggleIssue = (key) =>
    setCollapsedIssues((prev) =>
      prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key],
    );
  const [carePlanData, setCarePlanData] = useState(null);
  const [attendeesList, setAttendeesList] = useState([]);
  const [showEditWarning, setShowEditWarning] = useState(false);

  // Ref for print functionality
  const pdfRef = useRef();

  // Get care plan ID from location state
  const carePlanId = props.location?.state?.carePlanId;
  // Care plan type is passed in navigation state so the initial-score query can
  // start in parallel instead of waiting for GetCarePlanById to resolve first.
  const carePlanTypeFromState = props.location?.state?.carePlanType;
  const patientId = localStorage.getItem("patient");

  // Fetch care plan data
  const {
    data: carePlan,
    isLoading: isLoadingCarePlan,
    error: carePlanError,
  } = GetCarePlanById(carePlanId);

  // Prefer the type from navigation state (avoids the query waterfall); fall
  // back to the loaded care plan for direct navigation / page refresh.
  const carePlanType = carePlanTypeFromState || carePlan?.data?.type;
  const {
    data: initialScoreData,
    isLoading: isLoadingInitialScores,
  } = useCarePlanInitialScore(patientId, carePlanType);
  const initialScores = initialScoreData?.data || {};

  // The care-plan payload names the subheading but not the range it was scored
  // on, so the scale is resolved from the checklist heading it belongs to.
  const { data: functionalChecklistData } = GetFunctionalChecklist();
  const resolveScale = scaleResolverFor(
    functionalChecklistData?.data?.[0]?.headings || [],
  );

  // The "Initial Ind/Team Score" column is sourced from the separate
  // initial-score query, so keep the skeleton up until both queries settle to
  // avoid the empty-then-populated flash on the first row.
  const isLoadingContent = isLoadingCarePlan || isLoadingInitialScores;

  const getInitialScore = (subheadingId) =>
    subheadingId && initialScores[subheadingId]
      ? initialScores[subheadingId].score
      : null;
  const getInitialScoreDate = (subheadingId) =>
    subheadingId && initialScores[subheadingId]
      ? initialScores[subheadingId].assessmentDate
      : null;

  // Safe date parsing function
  // Strings with no timezone suffix are local time — normalise space→T so
  // browsers don't misinterpret them as UTC.
  const parseSafeDate = (dateString) => {
    if (!dateString) return null;
    try {
      const normalized =
        typeof dateString === "string" &&
        !dateString.includes("Z") &&
        !dateString.includes("+") &&
        !dateString.match(/-\d{2}:\d{2}$/)
          ? dateString.replace(" ", "T")
          : dateString;
      const date = new Date(normalized);
      return isNaN(date.getTime()) ? null : date;
    } catch (error) {
      console.error("Error parsing date:", dateString, error);
      return null;
    }
  };
  const getLocalISOString = () => {
      const now = new Date();
      const offset = -now.getTimezoneOffset();
      const sign = offset >= 0 ? "+" : "-";
      const absOffset = Math.abs(offset);
      const hh = String(Math.floor(absOffset / 60)).padStart(2, "0");
      const mm = String(absOffset % 60).padStart(2, "0");
      const pad = (n) => String(n).padStart(2, "0");
      return `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}T${pad(now.getHours())}:${pad(now.getMinutes())}:${pad(now.getSeconds())}${sign}${hh}:${mm}`;
    };

  // Format date for display
  const formatDate = (dateString) => {
    const date = parseSafeDate(dateString);
    return date ? moment(date).format("D MMM YYYY") : "N/A";
  };
  const formatTime = (dateString) => {
    const date = parseSafeDate(dateString);
    if (!date) return "N/A";
    // Render in Ontario, Canada time (America/Toronto). timeZoneName "short"
    // appends EST/EDT automatically based on daylight saving.
    return new Intl.DateTimeFormat("en-US", {
      hour: "numeric",
      minute: "2-digit",
      hour12: true,
      timeZone: "America/Toronto",
      timeZoneName: "short",
    }).format(date);
  };

  // Check if a functional issue has any meaningful data
  const hasIssueData = (issue) =>
    issue.currentScore != null ||
    issue.assignedDate != null ||
    issue.currentScore != null ||
    issue.currentScoreDate != null ||
    issue.targetScore != null ||
    (issue.comment != null &&
      issue.comment !== "No Comments" &&
      issue.comment !== "");

  // Transform care plan data for display
  useEffect(() => {
    if (carePlan && carePlan.data && !isLoadingCarePlan) {
      setCarePlanData(carePlan.data);

      // Set attendees list only for collaborative care plans
      if (
        carePlan.data.type === "COLLABORATIVE" &&
        carePlan.data.attendees &&
        carePlan.data.attendees.length > 0
      ) {
        setAttendeesList(carePlan.data.attendees);
      } else {
        setAttendeesList([]);
      }
    }
  }, [carePlan, isLoadingCarePlan]);

  const patientData = JSON.parse(localStorage.getItem("localPatient") || "{}");

  // Export to PDF function
  const handleExportPDF = async () => {
    if (!carePlanData) {
      toast.info("No care plan data available to export");
      return;
    }

    let pdfAssets;
    try {
      pdfAssets = await loadPdfAssets();
    } catch {
      toast.error("Unable to prepare the PDF for download");
      return;
    }

    // Switch to portrait
    const doc = new jsPDF("p", "mm", "a4");
    doc.addFileToVFS("Quicksand.ttf", pdfAssets.quicksandFontBase64);
    doc.addFont("Quicksand.ttf", "Quicksand", "normal");
    doc.setFont("Quicksand", "normal");
    const pageWidth = doc.internal.pageSize.width;

    // Scale factor: reduce all sizes by ~0.75 (landscape->portrait, fit more on page)
    const scale = 0.75;
    const contentMarginX = 20 * scale;

    addPdfHeader(doc, pdfAssets.logoDataUrl, patientData, carePlanData);

    let yPosition = 32;

    // Process headings and functional issues
    const visibleHeadings = (carePlanData.headings || []).filter((heading) =>
      heading.functionalIssues?.some(hasIssueData),
    );

    if (visibleHeadings.length > 0) {
      visibleHeadings.forEach((heading) => {
        // Check if we need a new page
        if (yPosition > 260 * scale) {
          doc.addPage();
          doc.setFillColor(255, 255, 255);
          doc.rect(
            0,
            0,
            doc.internal.pageSize.width,
            doc.internal.pageSize.height,
            "F",
          );
          yPosition = 20 * scale;
        }

        const marginX = contentMarginX;
        const headingBarHeight = 12 * scale;
        doc.setFillColor(93, 145, 168); // #5D91A8
        doc.rect(
          marginX,
          yPosition,
          pageWidth - 2 * marginX,
          headingBarHeight,
          "F",
        );
        doc.setTextColor(255, 255, 255);
        doc.setFontSize(11 * scale);
        doc.setFont("Quicksand", "normal");
        doc.text(heading.name, marginX + 8 * scale, yPosition + 8 * scale, {
          align: "left",
        });
        doc.setTextColor(0, 0, 0); // Reset to black for rest of content
        yPosition += headingBarHeight + 1 * scale;

        // Process functional issues
        const visibleIssues = (heading.functionalIssues || [])
          .filter(hasIssueData)
          .sort((a, b) => {
            const sa = a.currentScore, sb = b.currentScore;
            if (sa == null || sa === 'N/A') return 1;
            if (sb == null || sb === 'N/A') return -1;
            return sa - sb;
          });
        if (visibleIssues.length > 0) {
          // Check if we need a new page for the table
          if (yPosition > 220 * scale) {
            doc.addPage();
            doc.setFillColor(255, 255, 255);
            doc.rect(
              0,
              0,
              doc.internal.pageSize.width,
              doc.internal.pageSize.height,
              "F",
            );
            yPosition = 20 * scale;
          }

          // Build table data for ALL issues in this heading
          const tableData = [];
          const interventionRowIndexes = new Set();
          // The scale each table row's scores are on, by row index, so the
          // cell painters can colour a value from the right scale.
          const rowScales = [];

          visibleIssues.forEach((issue) => {
            // Add functional issue row
            rowScales[tableData.length] = resolveScale(issue.subheadingId);
            tableData.push([
              issue.subheadingName || "Functional Issue",
              issue.currentScore || "",
              issue.assignedDate ? formatDate(issue.assignedDate) : "",
              getInitialScore(issue.subheadingId) || "",
              getInitialScoreDate(issue.subheadingId) ? formatDate(getInitialScoreDate(issue.subheadingId)) : "",
              issue.targetScore || "",
              issue.comment && issue.comment !== "No Comments"
                ? issue.comment
                : "",
            ]);

            // Add intervention rows (indented)
            if (issue?.interventions && issue?.interventions.length > 0) {
              issue.interventions.forEach((intervention) => {
                // Format review date as 'Review: DD MMM YYYY' (no slashes)
                let reviewDate = "";
                if (intervention.reviewDate) {
                  const date = parseSafeDate(intervention.reviewDate);
                  if (date) {
                    reviewDate = `Review: ${moment(date).format(
                      "DD MMM YYYY",
                    )}`;
                  }
                }
                // Only show comment if present and not 'No Comments'
                const comment =
                  intervention.comment && intervention.comment !== "No Comments"
                    ? intervention.comment
                    : "";
                interventionRowIndexes.add(tableData.length);
                tableData.push([
                  intervention.labelName || "Intervention",
                  intervention?.assignedCareProvider?.lastName +
                    " " +
                    intervention?.assignedCareProvider?.firstName +
                    ": " +
                    intervention?.assignedCareProvider?.position?.abbreviation,
                  "", // Empty assigned date column
                  "", // Empty current score column
                  "", // Empty current score assigned date column
                  reviewDate,
                  comment,
                ]);
              });
            }
          });

          // Create ONE table for this entire heading
          doc.autoTable({
            startY: yPosition,
            head: [
              [
                "Functional Issue",
                carePlanData?.type === "COLLABORATIVE" ? "Current Team Score" : "Current Ind Score",
                "Assigned Date",
                carePlanData?.type === "COLLABORATIVE" ? "Initial Team Score" : "Initial Ind Score",
                "Assigned Date",
                carePlanData?.type === "COLLABORATIVE" ? "Target Team Score" : "Target Ind Score",
                "Comments",
              ],
            ],
            body: tableData,
            theme: "grid",
            headStyles: {
              fillColor: [175, 177, 184],
              textColor: [255, 255, 255],
              fontSize: 11 * scale,
              cellPadding: 2 * scale,
              font: "Quicksand",
              fontStyle: "normal",
              halign: "center",
              valign: "middle",
            },
            bodyStyles: {
              font: "Quicksand",
              fontSize: 11 * scale,
              cellPadding: 2 * scale,
              halign: "center",
              valign: "middle",
            },
            columnStyles: {
              0: { cellWidth: 97 * scale, halign: "left" },
              1: { cellWidth: 20 * scale },
              2: { cellWidth: 24 * scale },
              3: { cellWidth: 20 * scale },
              4: { cellWidth: 24 * scale },
              5: { cellWidth: 20 * scale },
              6: { cellWidth: 35 * scale },
            },
            margin: { left: marginX, right: marginX },
            tableWidth: pageWidth - 2 * marginX,
            didParseCell: function (data) {
              if (data.row.index === 0 && data.row.section === "head") {
                data.cell.styles.fillColor = [175, 177, 184];
                data.cell.styles.textColor = 255;
                data.cell.styles.lineWidth = 0.2;
              }

              // Check if this is an intervention row by looking at the first cell of the row
              const isInterventionRow = interventionRowIndexes.has(
                data.row.index,
              );
              const isScoreCell =
                !isInterventionRow && [1, 3, 5].includes(data.column.index);

              if (data.row.section !== "head") {
                if (!isInterventionRow) {
                  // Functional issue rows - light teal background
                  data.cell.styles.fillColor = [233, 246, 245]; // Very light teal #EAF6F5
                  data.cell.styles.textColor = 0; // Black text
                  data.cell.styles.cellPadding = 3 * scale;
                  data.cell.styles.lineWidth = 0;
                } else {
                  // Intervention rows - light gray background and indentation
                  data.cell.styles.fillColor = [245, 245, 245]; // Very light gray #F5F5F5
                  data.cell.styles.textColor = 0; // Black text
                  data.cell.styles.lineWidth = 0;

                  // Indent intervention rows in first column
                  if (data.column.index === 0) {
                    data.cell.styles.halign = "left";
                    data.cell.styles.cellPadding = {
                      left: 3 * scale,
                      right: 2 * scale,
                      top: 2 * scale,
                      bottom: 2 * scale,
                    };
                  }
                }

                if (
                  isScoreCell &&
                  getPdfScoreValue(
                    data.cell.raw,
                    rowScales[data.row.index],
                  ) !== null
                ) {
                  data.cell.text = [""];
                }
              }

              // Add bordered box for comments if present
              if (
                data.column.index === 6 &&
                data.cell.text &&
                data.cell.text.length > 0
              ) {
                // Draw a light gray border around comments
                const cell = data.cell;
                const x = cell.x;
                const y = cell.y;
                const width = cell.width;
                const height = cell.height;

                doc.setDrawColor(255, 255, 255); // Light gray border
                doc.setLineWidth(0.1);
                doc.rect(x, y, width, height, "S");
              }
            },
            // Draw only top and bottom borders for functional issue rows
            didDrawCell: function (data) {
              // Only apply to body section
              if (data.row.section !== "body") return;
              const isInterventionRow = interventionRowIndexes.has(
                data.row.index,
              );
              const isScoreCell =
                !isInterventionRow && [1, 3, 5].includes(data.column.index);
              if (isScoreCell) {
                drawPdfScoreCircle(
                  data.doc,
                  data.cell,
                  data.cell.raw,
                  scale,
                  rowScales[data.row.index],
                );
              }
              if (isInterventionRow) return;
              // Only draw for functional issue rows
              const cell = data.cell;
              const doc = data.doc;
              // Draw top border (thicker)
              doc.setDrawColor(255, 255, 255);
              doc.setLineWidth(1 * scale);
              if (typeof doc.setLineDash === "function") doc.setLineDash([]); // Reset dash if available
              doc.line(cell.x, cell.y, cell.x + cell.width, cell.y);
              // Draw bottom border (half thickness)
              doc.setLineWidth(1.5 * scale);
              doc.line(
                cell.x,
                cell.y + cell.height,
                cell.x + cell.width,
                cell.y + cell.height,
              );
            },
          });

          yPosition = doc.lastAutoTable
            ? doc.lastAutoTable.finalY + 2 * scale
            : yPosition + 2 * scale;
        }
      });
    }

    // Save the PDF
    const patientName = localStorage.getItem("patientName") || "Patient";
    const fileName = `Care_Plan_Conference_${patientName}_${moment().format(
      "YYYY-MM-DD",
    )}.pdf`;
    doc.save(fileName);
  };

  // Print function
  const handlePrint = useReactToPrint({
    contentRef: pdfRef,
    documentTitle: `Care_Plan_Conference_${moment().format("YYYY-MM-DD")}`,
  });

  const handleEditCarePlan = () => {
    if (!carePlanId) return;
    setShowEditWarning(true);
  };

  const handleCancelEdit = () => {
    setShowEditWarning(false);
  };

  const handleConfirmEdit = () => {
    if (!carePlanId) return;

    props.history.push({
      pathname: "/hcp/patient/care_review_plan",
      state: {
        carePlanId,
        fromOutstanding: true,
      },
    });
  };

  // Version history for the footer: amended versions (v2+) shown newest-first,
  // and the original (v1) used for the existing "Created by" line so it isn't
  // duplicated by the version list.
  const versions = carePlanData?.versions || [];
  const amendedVersions = versions
    .filter((v) => v.versionNumber > 1)
    .sort((a, b) => b.versionNumber - a.versionNumber);
  const originalVersion =
    versions.find((v) => v.versionNumber === 1) || carePlanData;

  return (
    <>
      <div className="container-fluid pt-12 pl-21 pb-12 pr-24 h-100 noScroll">
        <div className="card border-0 h-100">
          <div className="card-body verticalScroll">
            <div className="d-flex justify-content-between align-items-center">
              <h5 className="font-weight-lighter">
                {isLoadingCarePlan ? (
                  <Skeleton height={16} width={400} />
                ) : carePlanData ? (
                  carePlanData.type === "INDIVIDUAL" ? (
                    `HCP: Individual Care Plan: ${formatDate(
                      carePlanData?.entryDate,
                    )}`
                  ) : (
                    `HCP: Care Plan - ${
                      carePlanData?.typeOfMeeting
                    }: ${formatDate(carePlanData?.entryDate)}`
                  )
                ) : (
                  "Loading..."
                )}
              </h5>

              <div className="d-flex align-items-center">
                {/* {carePlanData && carePlanId && (
                  <button
                    type="button"
                    className="btn btn-outline-primary px-3 py-2 rounded mr-3"
                    onClick={handleEditCarePlan}
                  >
                    <FaEdit size={14} className="mr-2" />
                    <small className="fs-13">Edit Care Plan</small>
                  </button>
                )} */}

                {/* Attendees Dropdown - Only show for collaborative care plans */}
                {carePlanData && carePlanData.type === "COLLABORATIVE" && (
                  <div className="dropdown mr-3">
                    <button
                      className="btn px-3 py-2 d-flex align-items-center fs-15"
                      type="button"
                      id="attendeesDropdown"
                      data-toggle="dropdown"
                      aria-haspopup="true"
                      aria-expanded="false"
                    >
                      Attendees
                      <FaChevronDown
                        size={15}
                        className="ml-2"
                        style={{ color: "#51a193" }}
                      />
                    </button>
                    <div
                      className="dropdown-menu dropdown-menu-right"
                      aria-labelledby="attendeesDropdown"
                      style={{ minWidth: "250px" }}
                    >
                      <h6 className="dropdown-header">Conference Attendees</h6>
                      {attendeesList.length > 0 ? (
                        attendeesList.map((attendee, index) => (
                          <div
                            key={index}
                            className="dropdown-item-text px-3 py-1"
                          >
                            <small>
                              {index + 1}. {attendee.lastName || "N/A"},{" "}
                              {attendee.firstName || "N/A"}
                              {attendee.position?.abbreviation && (
                                <span className="text-muted ml-1">
                                  ({attendee.position.abbreviation})
                                </span>
                              )}
                            </small>
                          </div>
                        ))
                      ) : (
                        <div className="dropdown-item-text px-3 py-2">
                          <small className="text-muted">
                            No attendees listed
                          </small>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                <button
                  className="btn btn-outline-primary px-4 py-2 rounded py-1 d-none"
                  data-toggle="modal"
                  data-target="#filterModal"
                >
                  <FaFilter size={16} />
                  &nbsp;&nbsp; Filter
                </button>
              </div>
            </div>
            <hr />

            {/* Edit reasons — read-only history of every amended version (v2+),
                newest first, so the current version shows all prior reasons. */}
            {amendedVersions.filter((v) => v.editReason).length > 0 && (
              <div className="mb-3">
                {amendedVersions
                  .filter((v) => v.editReason)
                  .map((v) => (
                    <div key={v.id}>
                      <small>
                        <span className="font-weight-bold">
                          V{v.versionNumber}:
                        </span>{" "}
                        {v.editReason}
                      </small>
                    </div>
                  ))}
              </div>
            )}

            {isLoadingContent ? (
              <SkeletonTheme baseColor="#e9ecef" highlightColor="#f8f9fa">
                {/* Accordion Skeleton */}
                <div className="my-4">
                  {[1].map((accordionIndex) => (
                    <div key={accordionIndex} className="mb-2">
                      {/* Accordion Header */}
                      <div
                        className="accordion__primary accordion__button"
                        style={{
                          height: 45,
                          display: "flex",
                          alignItems: "center",
                          padding: "0 15px",
                          borderRadius: "4px",
                        }}
                      >
                        <small>
                          <Skeleton height={12} width={200} />
                        </small>
                      </div>

                      {/* Accordion Content */}
                      <div>
                        {/* Table Headers */}
                        <div className="row text-center mx-0 bg-white">
                          <div
                            className="col-md-3 px-0"
                            style={{ paddingLeft: "1px", paddingRight: "1px" }}
                          >
                            <div className="head_light text-white d-flex justify-content-center align-items-center h-100 py-1">
                              <small>Functional Issue</small>
                            </div>
                          </div>
                          <div
                            className="col px-0"
                            style={{ paddingLeft: "1px", paddingRight: "1px", order: 3 }}
                          >
                            <div
                              className="head_light text-white d-flex justify-content-center align-items-center h-100 py-1"
                              style={{ borderLeft: "1px solid #fff" }}
                            >
                              <small>{carePlanData?.type === "COLLABORATIVE" ? "Initial Team Score" : "Initial Ind Score"}</small>
                            </div>
                          </div>
                          <div
                            className="col px-0"
                            style={{ paddingLeft: "1px", paddingRight: "1px", order: 4 }}
                          >
                            <div
                              className="head_light text-white d-flex justify-content-center align-items-center h-100 py-1"
                              style={{ borderLeft: "1px solid #fff" }}
                            >
                              <small>Assigned Date</small>
                            </div>
                          </div>
                          <div
                            className="col px-0"
                            style={{ paddingLeft: "1px", paddingRight: "1px", order: 1 }}
                          >
                            <div
                              className="head_light text-white d-flex justify-content-center align-items-center h-100 py-1"
                              style={{ borderLeft: "1px solid #fff" }}
                            >
                              <small>{carePlanData?.type === "COLLABORATIVE" ? "Current Team Score" : "Current Ind Score"}</small>
                            </div>
                          </div>
                          <div
                            className="col px-0"
                            style={{ paddingLeft: "1px", paddingRight: "1px", order: 2 }}
                          >
                            <div
                              className="head_light text-white d-flex justify-content-center align-items-center h-100 py-1"
                              style={{ borderLeft: "1px solid #fff" }}
                            >
                              <small>Assigned Date</small>
                            </div>
                          </div>
                          <div
                            className="col px-0"
                            style={{ paddingLeft: "1px", paddingRight: "1px", order: 5 }}
                          >
                            <div
                              className="head_light text-white d-flex justify-content-center align-items-center h-100 py-1"
                              style={{ borderLeft: "1px solid #fff" }}
                            >
                              <small>{carePlanData?.type === "COLLABORATIVE" ? "Target Team Score" : "Target Ind Score"}</small>
                            </div>
                          </div>
                          <div
                            className="col-md-3 px-0"
                            style={{ paddingLeft: "1px", paddingRight: "1px", order: 6 }}
                          >
                            <div
                              className="head_light text-white d-flex justify-content-center align-items-center h-100 py-1"
                              style={{ borderLeft: "1px solid #fff" }}
                            >
                              <small>Comments</small>
                            </div>
                          </div>
                        </div>

                        {/* Functional Issue Rows */}
                        {[1, 2].map((issueIndex) => (
                          <Fragment key={issueIndex}>
                            <div className="row mx-0 bg_light pt-2 my-1">
                              <div
                                className="col-md-3 px-0 text-left"
                                style={{ marginLeft: "8px" }}
                              >
                                <a href="#" className="decoration-0">
                                  <FaPlus size={11} /> &nbsp;
                                  <small>
                                    <Skeleton height={12} width={120} />
                                  </small>
                                </a>
                              </div>
                              <div className="col px-1 text-center" style={{ order: 3 }}>
                                <p>
                                  <small>
                                    <Skeleton height={12} width={40} />
                                  </small>
                                </p>
                              </div>
                              <div className="col px-1 text-center" style={{ order: 4 }}>
                                <p>
                                  <small>
                                    <Skeleton height={12} width={80} />
                                  </small>
                                </p>
                              </div>
                              <div className="col px-1 text-center" style={{ order: 1 }}>
                                <p>
                                  <small>
                                    <Skeleton height={12} width={40} />
                                  </small>
                                </p>
                              </div>
                              <div className="col px-1 text-center" style={{ order: 2 }}>
                                <p>
                                  <small>
                                    <Skeleton height={12} width={80} />
                                  </small>
                                </p>
                              </div>
                              <div className="col px-1 text-center" style={{ order: 5 }}>
                                <p>
                                  <small>
                                    <Skeleton height={12} width={40} />
                                  </small>
                                </p>
                              </div>
                              <div className="col-md-3 text-center" style={{ order: 6 }}>
                                <button className="btn btn-outline-secondary btn-sm btn-block mr-2">
                                  <small>
                                    <Skeleton height={12} width="100%" />
                                  </small>
                                </button>
                              </div>
                            </div>

                            {/* Intervention Rows */}
                            {[1, 2].map((interventionIndex) => (
                              <div
                                key={interventionIndex}
                                className="row mx-1 align-items-center"
                                style={{
                                  backgroundColor:
                                    interventionIndex % 2 === 0
                                      ? "#F1F1F1"
                                      : "#F8F8F8",
                                }}
                              >
                                <div
                                  className="col-md-3 my-1"
                                  style={{ paddingLeft: "40px" }}
                                >
                                  <small>
                                    <Skeleton height={12} width={100} />
                                  </small>
                                </div>
                                <div className="col my-1">
                                  <small>
                                    <Skeleton height={12} width={120} />
                                  </small>
                                </div>
                                <div className="col my-1">
                                  <small>
                                    {/* Empty - Assigned Date column */}
                                  </small>
                                </div>
                                <div className="col my-1">
                                  <small>
                                    {/* Empty - Current Team Score column */}
                                  </small>
                                </div>
                                <div className="col my-1">
                                  <small>
                                    {/* Empty - Current Team Score Assigned Date column */}
                                  </small>
                                </div>
                                <div className="col my-1">
                                  <small>
                                    Review: <Skeleton height={12} width={80} />
                                  </small>
                                </div>
                                <div className="col-md-3 my-1">
                                  <button className="btn btn-outline-secondary btn-sm btn-block mr-2">
                                    <small>
                                      <Skeleton height={12} width="100%" />
                                    </small>
                                  </button>
                                </div>
                              </div>
                            ))}
                          </Fragment>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              </SkeletonTheme>
            ) : carePlanData && carePlanData.headings ? (
              <Accordion
                allowZeroExpanded
                allowMultipleExpanded
                className="my-4"
                preExpanded={["0"]}
              >
                {carePlanData.headings
                  .filter((heading) =>
                    heading.functionalIssues?.some(hasIssueData),
                  )
                  .map((heading, headingIndex) => (
                    <AccordionItem
                      key={headingIndex}
                      className="mb-2"
                      uuid={headingIndex.toString()}
                    >
                      <AccordionItemHeading className="accordion__primary">
                        <AccordionItemButton
                          style={{
                            height: 45,
                            display: "flex",
                            alignItems: "center",
                          }}
                        >
                          <small>{heading.name}</small>
                        </AccordionItemButton>
                      </AccordionItemHeading>
                      <AccordionItemPanel>
                        <div className="row text-center mx-0 py-0 bg-white">
                          <div
                            className="col-md-3 px-0"
                            style={{ paddingLeft: "1px", paddingRight: "1px" }}
                          >
                            <div className="head_light text-white d-flex justify-content-center align-items-center h-100 py-1">
                              <small>Functional Issue</small>
                            </div>
                          </div>
                          <div
                            className="col px-0"
                            style={{ paddingLeft: "1px", paddingRight: "1px", order: 3 }}
                          >
                            <div
                              className="head_light text-white d-flex justify-content-center align-items-center h-100 py-1"
                              style={{ borderLeft: "1px solid #fff" }}
                            >
                              <small>{carePlanData?.type === "COLLABORATIVE" ? "Initial Team Score" : "Initial Ind Score"}</small>
                            </div>
                          </div>
                          <div
                            className="col px-0"
                            style={{ paddingLeft: "1px", paddingRight: "1px", order: 4 }}
                          >
                            <div
                              className="head_light text-white d-flex justify-content-center align-items-center h-100 py-1"
                              style={{ borderLeft: "1px solid #fff" }}
                            >
                              <small>Assigned Date</small>
                            </div>
                          </div>
                          <div
                            className="col px-0"
                            style={{ paddingLeft: "1px", paddingRight: "1px", order: 1 }}
                          >
                            <div
                              className="head_light text-white d-flex justify-content-center align-items-center h-100 py-1"
                              style={{ borderLeft: "1px solid #fff" }}
                            >
                              <small>{carePlanData?.type === "COLLABORATIVE" ? "Current Team Score" : "Current Ind Score"}</small>
                            </div>
                          </div>
                          <div
                            className="col px-0"
                            style={{ paddingLeft: "1px", paddingRight: "1px", order: 2 }}
                          >
                            <div
                              className="head_light text-white d-flex justify-content-center align-items-center h-100 py-1"
                              style={{ borderLeft: "1px solid #fff" }}
                            >
                              <small>Assigned Date</small>
                            </div>
                          </div>
                          <div
                            className="col px-0"
                            style={{ paddingLeft: "1px", paddingRight: "1px", order: 5 }}
                          >
                            <div
                              className="head_light text-white d-flex justify-content-center align-items-center h-100 py-1"
                              style={{ borderLeft: "1px solid #fff" }}
                            >
                              <small>{carePlanData?.type === "COLLABORATIVE" ? "Target Team Score" : "Target Ind Score"}</small>
                            </div>
                          </div>
                          <div
                            className="col-md-3 px-0"
                            style={{ paddingLeft: "1px", paddingRight: "1px", order: 6 }}
                          >
                            <div
                              className="head_light text-white d-flex justify-content-center align-items-center h-100 py-1"
                              style={{ borderLeft: "1px solid #fff" }}
                            >
                              <small>Comments</small>
                            </div>
                          </div>
                        </div>
                        {heading.functionalIssues &&
                        heading.functionalIssues.filter(hasIssueData).length >
                          0 ? (
                          heading.functionalIssues
                            .filter(hasIssueData)
                            .sort((a, b) => {
                              const sa = a.currentScore, sb = b.currentScore;
                              if (sa == null || sa === 'N/A') return 1;
                              if (sb == null || sb === 'N/A') return -1;
                              return sa - sb;
                            })
                            .map((issue, issueIndex) => {
                              return(
                              <Fragment key={issueIndex}>
                                <div className="row mx-0 bg_light pt-2 my-1">
                                  <div
                                    className="col-md-3 px-0 text-left"
                                    style={{ marginLeft: "8px" }}
                                  >
                                    <a
                                      href="#"
                                      className="decoration-0"
                                      onClick={(e) => {
                                        e.preventDefault();
                                        toggleIssue(
                                          `${headingIndex}-${issueIndex}`,
                                        );
                                      }}
                                    >
                                      {isIssueExpanded(
                                        `${headingIndex}-${issueIndex}`,
                                      ) ? (
                                        <FaMinus />
                                      ) : (
                                        <FaPlus size={11} />
                                      )}{" "}
                                      &nbsp;
                                      <small>
                                        {issue.subheadingName ||
                                          "Functional Issue"}
                                      </small>
                                    </a>
                                  </div>
                                  <div className="col px-1 text-center d-flex align-items-center justify-content-center" style={{ order: 3 }}>
                                    <ScorePill
                                      value={getInitialScore(issue.subheadingId)}
                                      scale={resolveScale(issue.subheadingId)}
                                    />
                                  </div>
                                  <div className="col px-1 text-center" style={{ order: 4 }}>
                                    <p>
                                      <small>
                                        {formatDate(getInitialScoreDate(issue.subheadingId))}
                                      </small>
                                    </p>
                                  </div>
                                  <div className="col px-1 text-center d-flex align-items-center justify-content-center" style={{ order: 1 }}>
                                    <ScorePill
                                      value={issue.currentScore}
                                      scale={resolveScale(issue.subheadingId)}
                                    />
                                  </div>
                                  <div className="col px-1 text-center" style={{ order: 2 }}>
                                    <p>
                                      <small>
                                        {formatDate(issue.assignedDate)}
                                      </small>
                                    </p>
                                  </div>
                                  <div className="col px-1 text-center d-flex align-items-center justify-content-center" style={{ order: 5 }}>
                                    <ScorePill
                                      value={issue.targetScore}
                                      scale={resolveScale(issue.subheadingId)}
                                    />
                                  </div>
                                  <div className="col-md-3 text-center" style={{ order: 6 }}>
                                    <button className="btn btn-outline-secondary btn-sm btn-block mr-2">
                                      <small>
                                        {issue.comment || "No Comments"}
                                      </small>
                                    </button>
                                  </div>
                                </div>
                                {isIssueExpanded(
                                  `${headingIndex}-${issueIndex}`,
                                ) &&
                                  issue.interventions &&
                                  issue.interventions.length > 0 &&
                                  issue.interventions.map(
                                    (intervention, interventionIndex) => (
                                  <div
                                    key={interventionIndex}
                                    className="row mx-0 align-items-center"
                                    style={{
                                      backgroundColor:
                                        interventionIndex % 2 === 0
                                              ? "#F1F1F1"
                                              : "#F8F8F8",
                                        }}
                                      >
                                        <div
                                          className="col-md-3 my-1"
                                          style={{ paddingLeft: "24px" }}
                                        >
                                          <small>
                                            {intervention.labelName ||
                                              "N/A"}
                                          </small>
                                        </div>
                                        <div
                                          className="col my-1"
                                          style={{
                                            flexGrow: 2,
                                            paddingLeft: "40px",
                                          }}
                                        >
                                          <small>
                                            {intervention.assignedCareProvider
                                              ? `${
                                                  intervention
                                                    .assignedCareProvider
                                                    .lastName
                                                }, ${
                                                  intervention
                                                    .assignedCareProvider
                                                    .firstName
                                                }: ${
                                                  intervention
                                                    .assignedCareProvider
                                                    .position?.abbreviation ||
                                                  "N/A"
                                                }`
                                              : "N/A"}
                                          </small>
                                        </div>
                                        <div className="col my-1">
                                          <small>
                                            {/* Empty - Current Team Score column */}
                                          </small>
                                        </div>
                                        <div
                                          className="col my-1"
                                          style={{
                                            minWidth: "150px",
                                            whiteSpace: "nowrap",
                                          }}
                                        >
                                          <small>
                                            Review:{" "}
                                            {formatDate(
                                              intervention.reviewDate,
                                            )}
                                          </small>
                                        </div>
                                        <div className="col-md-3 my-1">
                                          <button className="btn btn-outline-secondary btn-sm btn-block mr-2">
                                            <small>
                                              {intervention.comment ||
                                                "No Comments"}
                                            </small>
                                          </button>
                                        </div>
                                      </div>
                                    ),
                                  )}
                              </Fragment>
                            )})
                        ) : (
                          <div className="row text-center mx-0 my-3">
                            <div className="col-12">
                              <p className="text-muted">
                                No functional issues found for this heading
                              </p>
                            </div>
                          </div>
                        )}
                      </AccordionItemPanel>
                    </AccordionItem>
                  ))}
              </Accordion>
            ) : (
              <div className="text-center my-4">
                <p className="text-muted">No care plan data available</p>
              </div>
            )}
          </div>
          <div className="d-flex flex-column align-items-end pr-4 py-2">
            {/* Amended versions (v2+), newest first */}
            {amendedVersions.map((v) => (
              <span
                key={v.id}
                className="fs-13 font-weight-normal"
                title={v.editReason || undefined}
              >
                {v.id === carePlanId ? (
                  <span className="font-weight-bold">
                    Version {v.versionNumber}
                  </span>
                ) : (
                  <Link
                    to={{
                      pathname: "/hcp/patient/weekly_conference",
                      state: { carePlanId: v.id },
                    }}
                  >
                    Version {v.versionNumber}
                  </Link>
                )}{" "}
                Modified By{" "}
                <span>
                  {v.author?.lastName} {v.author?.firstName} :{" "}
                  {v.author?.position?.abbreviation}
                </span>
                <span className="mx-2">
                  {formatDate(v.signedAt || v.dateCreated)}
                </span>
                <span>{formatTime(v.signedAt || v.dateCreated)}</span>
              </span>
            ))}
            {/* Original (v1) */}
            <span className="fs-13 font-weight-normal">
              {originalVersion?.id === carePlanId ? (
                <span className="font-weight-bold">Original Version</span>
              ) : (
                <Link
                  to={{
                    pathname: "/hcp/patient/weekly_conference",
                    state: { carePlanId: originalVersion?.id },
                  }}
                >
                  Original Version
                </Link>
              )}{" "}
              Created by:{" "}
              <span>
                {originalVersion?.author?.lastName}{" "}
                {originalVersion?.author?.firstName} :{" "}
                {originalVersion?.author?.position?.abbreviation}
              </span>
              <span className="mx-2">
                {formatDate(originalVersion?.dateCreated)}
              </span>
              <span>{formatTime(originalVersion?.dateCreated)}</span>
            </span>
          </div>
        </div>
      </div>
      <CustomFooter
        onDownload={carePlanData ? handleExportPDF : null}
        onEdit={carePlanData && carePlanId ? handleEditCarePlan : null}
        showSignButton={false}
      />

      <div
        className={`modal fade ${showEditWarning ? "show d-block" : ""}`}
        tabIndex="-1"
        role="dialog"
        aria-modal={showEditWarning}
        aria-hidden={!showEditWarning}
        style={{
          backgroundColor: showEditWarning
            ? "rgba(0,0,0,0.5)"
            : "transparent",
        }}
      >
        <div
          className="modal-dialog modal-dialog-centered"
          role="document"
          style={{ width: "395px", maxWidth: "395px" }}
        >
          <div
            className="modal-content"
            style={{
              minHeight: "207px",
              padding: "16px",
              border: 0,
              borderRadius: "16px",
              display: "flex",
              flexDirection: "column",
            }}
          >
            <div className="d-flex justify-content-end">
              <button
                type="button"
                className="btn p-0"
                onClick={handleCancelEdit}
                aria-label="Close edit confirmation"
              >
                <FaTimes
                  className="text_primary"
                  size={18}
                  style={{ strokeWidth: 1 }}
                />
              </button>
            </div>
            <div
              className="modal-body text-center p-0"
              style={{
                flex: "1 1 auto",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <p className="text_default fs-15 mb-0">
                Are you sure you want to edit
                <br />
                this document?
              </p>
            </div>
            <div className="modal-footer border-0 d-flex justify-content-between p-0">
              <button
                type="button"
                className="btn btn_outline_danger overlay-btn small-text"
                onClick={handleCancelEdit}
              >
                GO BACK
              </button>
              <button
                type="button"
                className="btn btn-outline-primary overlay-btn small-text"
                onClick={handleConfirmEdit}
              >
                YES
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Hidden Print Component */}
      <div style={{ display: "none" }}>
        <div
          style={{
            width: "300",
            minHeight: "300mm",
            background: "#fff",
            zIndex: -1,
            top: 0,
          }}
          ref={pdfRef}
          id="pdf-content"
        >
          <div
            style={{
              padding: 20,
              fontFamily: "Arial, sans-serif",
              fontSize: 14,
            }}
          >
            {/* Patient Data, Title, and Date in a Flex Row */}
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                marginBottom: "10px",
              }}
            >
              {/* Patient Data Section */}
              <div style={{ minWidth: 180 }}>
                <div
                  style={{ fontSize: 14, fontWeight: "bold", color: "#222" }}
                >
                  Patient
                </div>
                <div style={{ fontSize: 12, color: "#222", marginTop: 2 }}>
                  {patientData?.lastName}, {patientData?.firstName}
                </div>
                <div style={{ fontSize: 12, color: "#222", marginTop: 2 }}>
                  {patientData?.age} {patientData?.gender}
                </div>
                <div style={{ fontSize: 12, color: "#222", marginTop: 2 }}>
                  {patientData?.dob
                    ? moment(patientData?.dob).format("DD MMM YYYY")
                    : ""}
                </div>
                <div style={{ fontSize: 12, color: "#222", marginTop: 2 }}>
                  POMMe ID: {patientData?.pommeId}
                </div>
              </div>

              {/* Title/Header */}
              <div style={{ textAlign: "center", flex: 1 }}>
                <div
                  style={{ fontSize: 18, fontWeight: "bold", marginBottom: 5 }}
                >
                  {carePlanData && carePlanData.type === "INDIVIDUAL"
                    ? `(${
                        carePlanData.author?.position?.abbreviation || "N/A"
                      }) Care Plan`
                    : "Collaborative Care Plan"}
                </div>

                <p>{`Created by ${
                  carePlanData?.author
                    ? `${carePlanData.author?.lastName}, ${carePlanData.author?.firstName}`
                    : "Unknown"
                }: ${
                  carePlanData?.author?.position?.abbreviation || "N/A"
                }  ${formatInEt(
                  carePlanData?.entryDate,
                  "DD MMM YYYY HH:mm"
                )}`}</p>
              </div>

              {/* Attendees Section for Collaborative Care Plans */}
              {carePlanData &&
                carePlanData.type === "COLLABORATIVE" &&
                attendeesList.length > 0 && (
                  <div style={{ marginBottom: 25 }}>
                    <div
                      style={{
                        fontSize: 16,
                        fontWeight: "bold",
                        marginBottom: 10,
                        color: "#333",
                        paddingBottom: 5,
                      }}
                    >
                      Attendees
                    </div>
                    {attendeesList.map((attendee, index) => (
                      <p
                        key={index}
                        style={{
                          fontSize: 12,
                          margin: "3px 0",
                          paddingLeft: 10,
                        }}
                      >
                        {index + 1}. {attendee.lastName || "N/A"},{" "}
                        {attendee.firstName || "N/A"}
                      </p>
                    ))}
                  </div>
                )}
            </div>

            {/* Care Plan Data */}
            {carePlanData &&
              carePlanData.headings &&
              carePlanData.headings
                .filter((heading) =>
                  heading.functionalIssues?.some(hasIssueData),
                )
                .map((heading, headingIndex) => (
                  <div key={headingIndex} style={{ marginBottom: 5 }}>
                    <div
                      style={{
                        background: "#5D91A8",
                        color: "#fff",
                        padding: "8px 12px",
                        borderRadius: 4,
                        marginBottom: 5,
                      }}
                    >
                      {heading.name}
                    </div>

                    {heading.functionalIssues &&
                    heading.functionalIssues.filter(hasIssueData).length > 0 ? (
                      <table
                        style={{
                          width: "100%",
                          borderCollapse: "collapse",
                          marginBottom: 10,
                        }}
                      >
                        <thead>
                          <tr
                            style={{
                              marginBottom: 5,
                              border: "1px solid #fff",
                              height: "11px",
                            }}
                          >
                            {[
                              "Functional Issue",
                              carePlanData?.type === "COLLABORATIVE" ? "Current Team Score" : "Current Ind Score",
                              "Assigned Date",
                              carePlanData?.type === "COLLABORATIVE" ? "Initial Team Score" : "Initial Ind Score",
                              "Assigned Date",
                              carePlanData?.type === "COLLABORATIVE" ? "Target Team Score" : "Target Ind Score",
                              "Comments",
                            ].map((header, index) => (
                              <th
                                key={index}
                                style={{
                                  width:
                                    index === 0
                                      ? "22%"
                                      : index === 6
                                        ? "18%"
                                        : "12%",
                                  background: "#AFB1B8",
                                  color: "#fff",
                                  fontWeight: "bold",
                                  border: "1px solid #fff",
                                  padding: 8,
                                  textAlign: "center",
                                  verticalAlign: "center",
                                }}
                              >
                                {header}
                              </th>
                            ))}
                          </tr>
                        </thead>
                        <tbody>
                          {heading.functionalIssues
                            .filter(hasIssueData)
                            .sort((a, b) => {
                              const sa = a.currentScore, sb = b.currentScore;
                              if (sa == null || sa === 'N/A') return 1;
                              if (sb == null || sb === 'N/A') return -1;
                              return sa - sb;
                            })
                            .map((issue, issueIndex) => (
                              <React.Fragment key={issueIndex}>
                                <tr
                                  style={{
                                    background: "#EAF6F5",
                                    color: "#000",
                                  }}
                                >
                                  <td
                                    style={{
                                      border: "1px solid #fff",
                                      padding: 8,
                                    }}
                                  >
                                    {issue.subheadingName || ""}
                                  </td>
                                  <td
                                    style={{
                                      border: "1px solid #fff",
                                      padding: 8,
                                    }}
                                  >
                                    {issue.currentScore || ""}
                                  </td>
                                  <td
                                    style={{
                                      border: "1px solid #fff",
                                      padding: 8,
                                    }}
                                  >
                                    {formatDate(issue.assignedDate)}
                                  </td>
                                  <td
                                    style={{
                                      border: "1px solid #fff",
                                      padding: 8,
                                    }}
                                  >
                                    {getInitialScore(issue.subheadingId) || ""}
                                  </td>
                                  <td
                                    style={{
                                      border: "1px solid #fff",
                                      padding: 8,
                                    }}
                                  >
                                    {formatDate(getInitialScoreDate(issue.subheadingId))}
                                  </td>
                                  <td
                                    style={{
                                      border: "1px solid #fff",
                                      padding: 8,
                                    }}
                                  >
                                    {issue.targetScore || ""}
                                  </td>
                                  <td
                                    style={{
                                      border: "1px solid #fff",
                                      padding: 8,
                                      background: "#EAF6F5",
                                    }}
                                  >
                                    {issue.comment &&
                                    issue.comment !== "No Comments"
                                      ? issue.comment
                                      : ""}
                                  </td>
                                </tr>
                                {issue.interventions &&
                                  issue.interventions.length > 0 &&
                                  issue.interventions.map(
                                    (intervention, interventionIndex) => (
                                      <tr
                                        key={interventionIndex}
                                        style={{
                                          background: "#F5F5F5",
                                          color: "#000",
                                          textAlign: "center",
                                        }}
                                      >
                                        <td
                                          style={{
                                            border: "1px solid #fff",
                                            padding: "8px 8px 8px 28px",
                                            textAlign: "center",
                                          }}
                                        >
                                          {intervention.labelName ||
                                            "Intervention"}
                                        </td>
                                        <td
                                          style={{
                                            border: "1px solid #fff",
                                            padding: 8,
                                          }}
                                        >
                                          {intervention.assignedCareProvider
                                            ? `${
                                                intervention
                                                  .assignedCareProvider.lastName
                                              } ${
                                                intervention
                                                  .assignedCareProvider
                                                  .firstName
                                              }: ${
                                                intervention
                                                  .assignedCareProvider
                                                  ?.position?.abbreviation ||
                                                "N/A"
                                              }`
                                            : ""}
                                        </td>
                                        <td
                                          style={{
                                            border: "1px solid #fff",
                                            padding: 8,
                                          }}
                                        ></td>
                                        <td
                                          style={{
                                            border: "1px solid #fff",
                                            padding: 8,
                                          }}
                                        ></td>
                                        <td
                                          style={{
                                            border: "1px solid #fff",
                                            padding: 8,
                                          }}
                                        ></td>
                                        <td
                                          style={{
                                            border: "1px solid #fff",
                                            padding: 8,
                                            minWidth: 150,
                                            whiteSpace: "nowrap",
                                          }}
                                        >
                                          {intervention.reviewDate
                                            ? `Review: ${formatDate(
                                                intervention.reviewDate,
                                              )}`
                                            : ""}
                                        </td>
                                        <td
                                          style={{
                                            border: "1px solid #fff",
                                            padding: 8,
                                          }}
                                        >
                                          {intervention.comment &&
                                          intervention.comment !== "No Comments"
                                            ? intervention.comment
                                            : ""}
                                        </td>
                                      </tr>
                                    ),
                                  )}
                              </React.Fragment>
                            ))}
                        </tbody>
                      </table>
                    ) : (
                      <p
                        style={{
                          fontStyle: "italic",
                          color: "#666",
                          textAlign: "center",
                          padding: 20,
                        }}
                      >
                        No functional issues found for this heading
                      </p>
                    )}
                  </div>
                ))}

            {!carePlanData && (
              <div style={{ marginBottom: 25 }}>
                <p
                  style={{
                    fontStyle: "italic",
                    color: "#666",
                    textAlign: "center",
                    padding: 20,
                  }}
                >
                  No care plan data available
                </p>
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default WeekConference;
